import React, { useState } from 'react';
import {
  findStripePrice,
  formatStripePrice,
  type PublicStripePrice,
} from '@/utils/formatStripePrice';
import type { PortalDownsellFailureReason } from '@/api/portal';
import {
  isDownsellEligible,
  usePortalActions,
  usePortalBlockPresence,
  usePortalDownsellOfferRegistration,
  type DownsellOfferConfig,
} from './portalActionsContext';
import { PortalInvite } from './PortalEntryRequestForm';
import { getPublicQuizStrings } from '@/utils/publicQuizStrings';
import { resolvePortalText } from '@/utils/portalLegacyDefaults';
import { useInertPreviewProps } from './inertPreview';
import { useEditorPreview } from '../EditorPreviewContext';
import type { DownsellOfferAppearance } from '../semanticTextRoles';
import { pickStrings } from '@/i18n';
import { editorStrings } from '@/i18n/dictionaries/editor';
import { offerCtaTextColor } from './offerCtaTextColor';

/**
 * WEB-1152 (П9) атом 3 — блок `downsell_offer` портала отмены (удержание).
 *
 * По образцу downsell_popup (WEB-712), но для портала: конфиг несёт
 * targetPriceId (дешёвая цена ЭТОГО проекта) и условие «показывать для этих
 * исходных цен» (sourcePriceIds; пусто = для всех). Кнопка «принять скидку»
 * зовёт POST /public/portal/downsell; после успеха — «скидка применится со
 * следующего периода» (proration none, оплаченный период доживает по старой
 * цене). Блок никогда не заслоняет кнопку отмены (комплаенс — cancel_button
 * рендерится независимо).
 */
export interface DownsellOfferBlockProps {
  title?: string;
  text?: string;
  ctaLabel?: string;
  successText?: string;
  targetPriceId?: string;
  /** Показывать оффер только владельцам этих исходных цен; пусто = всем. */
  sourcePriceIds?: string[];
  stripePrices?: PublicStripePrice[];
  primaryColor?: string;
  /**
   * WEB-1692 юнит 5 — накладки оформления, заданные автором (пустые объекты,
   * пока он ничего не задал). Считает их одна функция на обе двери карточки
   * (см. `semanticTextRoles.ts`): вторая дверь — шаг подтверждения отмены.
   */
  appearance?: DownsellOfferAppearance;
  /**
   * WEB-1905: язык ВОРОНКИ. Фолбэки заголовка и кнопки, отказы и текст успеха
   * идут из словаря плеера по нему — раньше это были английские константы.
   */
  language?: string | null;
}

/**
 * WEB-1753: подписи предпросмотра — на языке КАБИНЕТА, потому что их читает
 * АВТОР в редакторе, а не покупатель. Публичные строки блока живут в словаре
 * плеера (язык воронки) и здесь не дублируются.
 */
const EDITOR_NOTE_NO_ACCESS = (): string =>
  pickStrings(editorStrings).portalInvalidLinkPreviewHint;
const EDITOR_NOTE_EMPTY = (): string =>
  pickStrings(editorStrings).portalNothingToOfferHint;

const cardStyle: React.CSSProperties = {
  border: '1px solid #e5e5e5',
  borderRadius: 12,
  padding: 16,
  marginBottom: 16,
  textAlign: 'left',
};

/**
 * WEB-1692 юнит 5. Заливка ВНУТРЕННЕЙ ПЛАШКИ — репорт владельца 24.08: «сейчас
 * можно залить только наружный контейнер, а внутреннюю часть плашки, в которой
 * всё написано и в которой находится кнопка, залить нечем». Наружный контейнер
 * — это общий «Фон блока» (WEB-1062), он остаётся своим полем.
 *
 * Накладка спредится ПОСЛЕ прежних строк и при пустом поле равна `{}`: вид
 * старой воронки не меняется ни на байт.
 */
const cardWith = (
  appearance?: DownsellOfferAppearance,
): React.CSSProperties => ({
  ...cardStyle,
  ...(appearance?.cardBackgroundColor
    ? { background: appearance.cardBackgroundColor }
    : {}),
});

/**
 * Надпись кнопки и её заливка — СВОИ поля, а не общий сборщик кнопок
 * `buttonSurfaceStyle`: у этого блока `innerTextColor` уже красит весь текст,
 * и отдать его кнопке значило бы не дать владельцу отдельного цвета надписи
 * (он просил именно отдельный) и перекрасить кнопку в живых воронках. Разбор —
 * в semantic-text-roles.ts на бэке.
 */
const ctaStyle = (
  primaryColor?: string,
  appearance?: DownsellOfferAppearance,
): React.CSSProperties => {
  const background =
    appearance?.ctaBackgroundColor ?? primaryColor ?? '#1677ff';
  // WEB-1805 (живой случай Д48): надпись была прибита строкой '#fff' — на
  // светлой заливке (белый акцент темы, свой светлый цвет автора) выходила
  // белая кнопка с белой надписью, замерено вычисленным стилем на денежном
  // экране. Считаем по той же заливке, что рисуем, и тем же приёмом, что у
  // соседей (`opaqueContrastBaseOf` → `pickOnPrimaryColor`): плёнка и
  // неизмеримое (`var(--…)`) оставляют исторический белый, потому что по ним
  // судить нечем.
  return {
    display: 'block',
    width: '100%',
    marginTop: 12,
    padding: '10px 16px',
    borderRadius: 8,
    border: 'none',
    background,
    color: offerCtaTextColor(background),
    cursor: 'pointer',
    fontSize: 14,
    fontWeight: 600,
    ...(appearance?.cta ?? {}),
  };
};

export function DownsellOfferBlock({
  title,
  text,
  ctaLabel,
  successText,
  targetPriceId,
  sourcePriceIds,
  stripePrices,
  primaryColor,
  appearance,
  language,
}: DownsellOfferBlockProps) {
  const t = getPublicQuizStrings(language).portal;
  const portal = usePortalActions();
  // Регресс-фикс: блок объявляет себя видимым — если на экране он один, именно
  // он печатает приглашение войти по почте (иначе экран остался бы пустым).
  const isInviteHost = usePortalBlockPresence();
  const [busy, setBusy] = useState(false);
  // Ревизия 2 (дефект 1): причина отказа, а не булево «упало» — 409 от
  // бэкенда терминален, и предлагать повтор в нём нельзя.
  const [failure, setFailure] = useState<PortalDownsellFailureReason | null>(
    null,
  );
  const [accepted, setAccepted] = useState(false);

  // WEB-1905 (остаток приёмки 07.09): старые английские умолчания из
  // содержимого не перебивают словарь (`utils/portalLegacyDefaults.ts`).
  const resolvedTitle = resolvePortalText(title, t.offerTitle);
  const resolvedCta = resolvePortalText(ctaLabel, t.offerCta);
  const targetPrice = findStripePrice(stripePrices, targetPriceId);
  const targetPriceLabel = targetPrice ? formatStripePrice(targetPrice) : null;

  // WEB-1183: заявка провайдеру о своей конфигурации — шаг подтверждения
  // отмены (CancelSubscriptionButton) переиспользует её, чтобы показать
  // тот же оффер в момент намерения уйти. Без provider/targetPriceId
  // предлагать нечего — заявка снимается (null).
  const offerConfig: DownsellOfferConfig | null =
    portal && targetPriceId
      ? {
          title,
          text,
          ctaLabel,
          successText,
          targetPriceId,
          sourcePriceIds,
          stripePrices,
          primaryColor,
          // WEB-1692 юнит 5: оформление едет ко ВТОРОЙ двери — карточке оффера
          // на шаге подтверждения отмены (CancelSubscriptionButton).
          appearance,
        }
      : null;
  usePortalDownsellOfferRegistration(offerConfig);
  // WEB-1723: инертность макета без `disabled` — выключенная кнопка гасила
  // клик по карточке оффера, и блок не выделялся (см. inertPreview.ts).
  const inertProps = useInertPreviewProps();
  // WEB-1753: выбранный автором вид — ТОЛЬКО для канваса редактора. Условие
  // на isEditorPreview держит границу: в опубликованном портале (и на любой
  // публичной странице без провайдера портала) значение игнорируется и ветка
  // остаётся прежней.
  const { isEditorPreview, downsellOfferPreviewState } = useEditorPreview();
  const previewState = isEditorPreview ? downsellOfferPreviewState : 'offer';

  if (!portal) {
    // Канвас редактора — инертное превью. WEB-1753: какой из четырёх видов
    // рисовать, говорит переключатель панели свойств; вне канваса редактора
    // (isEditorPreview ложь) значение не читается вовсе — вид всегда прежний.
    if (isEditorPreview && previewState === 'accepted') {
      // Зеркало живой ветки `accepted` ниже: тот же текст автора и тот же
      // дефолт t.downsellSuccess. Отдельный testid — чтобы сторож
      // не путал предпросмотр с настоящим успехом покупателя.
      return (
        <p
          data-testid="portal-downsell-preview-success"
          style={{ marginBottom: 16, ...(appearance?.secondary ?? {}) }}
        >
          {successText || t.downsellSuccess}
        </p>
      );
    }
    if (isEditorPreview && previewState === 'no_access') {
      // Зеркало живой ветки `!portal.hasAccess`: покупатель без доступа видит
      // приглашение войти по почте. Сам PortalInvite без провайдера портала
      // молчит (форма запроса ссылки зовёт портальные ручки), поэтому на
      // канвасе печатается ТА ЖЕ словарная строка `portal.invalidLink` — своей
      // копии текста здесь нет, иначе она разошлась бы с живой.
      return (
        <div
          data-testid="portal-downsell-preview-no-access"
          style={cardWith(appearance)}
        >
          <p>{t.invalidLink}</p>
          <p style={{ marginTop: 8, fontSize: 12, color: '#8c8c8c' }}>
            {EDITOR_NOTE_NO_ACCESS()}
          </p>
        </div>
      );
    }
    if (isEditorPreview && previewState === 'empty') {
      // Живая ветка «предлагать нечего» возвращает null — покупателю блока
      // не видно. На канвасе так нельзя: пустое место автор не выберет и не
      // поймёт. Печатаем подсказку — она и есть честный ответ «здесь пусто».
      return (
        <div
          data-testid="portal-downsell-preview-empty"
          style={{
            ...cardWith(appearance),
            borderStyle: 'dashed',
            color: '#8c8c8c',
          }}
        >
          <p>{EDITOR_NOTE_EMPTY()}</p>
        </div>
      );
    }
    return (
      <div data-testid="portal-downsell-preview" style={cardWith(appearance)}>
        <p style={{ fontWeight: 700, ...(appearance?.title ?? {}) }}>
          {resolvedTitle}
        </p>
        {text && (
          <p style={{ marginTop: 4, ...(appearance?.secondary ?? {}) }}>
            {text}
          </p>
        )}
        {targetPriceLabel && (
          <p
            style={{
              marginTop: 4,
              color: '#8c8c8c',
              ...(appearance?.secondary ?? {}),
            }}
          >
            {targetPriceLabel}
          </p>
        )}
        <button
          type="button"
          style={ctaStyle(primaryColor, appearance)}
          {...inertProps}
        >
          {resolvedCta}
        </button>
      </div>
    );
  }

  if (portal.loading) return null;
  // Дефект 5: страница может состоять из одного оффера — тогда приглашение
  // войти по почте печатает он (иначе тупик: обещания формы нет и её нет).
  // WEB-1192 (п.3): hasAccess вместо guid из URL — креденшел в провайдере.
  if (!portal.hasAccess)
    return <PortalInvite active={isInviteHost} language={language} />;
  if (portal.error || !targetPriceId) return null;

  if (accepted) {
    return (
      <p
        data-testid="portal-downsell-success"
        style={{ marginBottom: 16, ...(appearance?.secondary ?? {}) }}
      >
        {successText || t.downsellSuccess}
      </p>
    );
  }

  // Целевая подписка. Ревизия десятки 7: условие — ТОЛЬКО через общий
  // isDownsellEligible (то же, что на шаге подтверждения отмены, WEB-1183);
  // раньше здесь жила инлайновая копия тех же четырёх проверок — комментарий
  // у isDownsellEligible обещал «одно условие», а кода-то было два.
  const eligible = offerConfig
    ? portal.subscriptions.find((s) => isDownsellEligible(s, offerConfig))
    : undefined;
  if (!eligible) return null;

  const onAccept = async () => {
    if (busy) return;
    setBusy(true);
    setFailure(null);
    const outcome = await portal.downsell(
      eligible.subscriptionId,
      targetPriceId,
    );
    setBusy(false);
    if (outcome.ok) {
      setAccepted(true);
    } else {
      setFailure(outcome.reason);
    }
  };

  // Терминальные отказы (409): кнопку убираем — повтор не сработает НИКОГДА,
  // а раньше блок звал жать снова и снова («Please try again»).
  const terminal =
    failure === 'subscription_cancelling' ||
    failure === 'offer_not_configured' ||
    // WEB-2457 п.4: скидка закрыта тарифом клиента — повтор не поможет.
    failure === 'downsell_unavailable';
  const noteStyle: React.CSSProperties = {
    marginTop: 8,
    fontSize: 13,
    color: '#cf1322',
  };

  return (
    <div data-testid="portal-downsell-offer" style={cardWith(appearance)}>
      <p style={{ fontWeight: 700, ...(appearance?.title ?? {}) }}>
        {resolvedTitle}
      </p>
      {text && (
        <p style={{ marginTop: 4, ...(appearance?.secondary ?? {}) }}>{text}</p>
      )}
      {targetPriceLabel && (
        <p
          style={{
            marginTop: 4,
            color: '#8c8c8c',
            ...(appearance?.secondary ?? {}),
          }}
        >
          {t.newPrice(targetPriceLabel)}
        </p>
      )}
      {!terminal && (
        <button
          type="button"
          style={{
            ...ctaStyle(primaryColor, appearance),
            opacity: busy ? 0.6 : 1,
          }}
          disabled={busy}
          onClick={() => void onAccept()}
        >
          {resolvedCta}
        </button>
      )}
      {failure === 'subscription_cancelling' && (
        // Дефект 3: код один на три состояния (отмена запланирована /
        // отменена / пауза) — testid и текст больше не называют отмену.
        <p data-testid="portal-downsell-not-eligible" style={noteStyle}>
          {t.downsellNotEligible}
        </p>
      )}
      {(failure === 'offer_not_configured' ||
        failure === 'downsell_unavailable') && (
        <p data-testid="portal-downsell-unavailable" style={noteStyle}>
          {t.downsellUnavailable}
        </p>
      )}
      {failure === 'unavailable' && <p style={noteStyle}>{t.downsellError}</p>}
    </div>
  );
}
